import React from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  SparklesIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { Subscription } from '../../types'
import CreatorAvatar from './CreatorAvatar'
import { BadgeKind, displayName, formatCents, priceCents, statusBadge } from './subscriptionsView'

interface SubscriptionCardProps {
  subscription: Subscription
  onCancel?: (subscription: Subscription) => void
  onReactivate?: (subscription: Subscription) => void
  /** Stripe's billing portal: invoices, card, plan. */
  onManage?: (subscription: Subscription) => void
  /** Past due: go fix the card. */
  onUpdateCard?: (subscription: Subscription) => void
}

const day = (iso: string) => format(new Date(iso), 'MMM d, yyyy')

const BADGES: Record<BadgeKind, { className: string; Icon: typeof CheckCircleIcon }> = {
  active: { className: 'bg-green-50 text-green-700 border-green-200', Icon: CheckCircleIcon },
  trial: { className: 'bg-blue-50 text-blue-700 border-blue-200', Icon: SparklesIcon },
  cancels: { className: 'bg-amber-50 text-amber-700 border-amber-200', Icon: ClockIcon },
  past_due: { className: 'bg-red-50 text-red-700 border-red-200', Icon: ExclamationTriangleIcon },
  paused: { className: 'bg-gray-100 text-gray-600 border-gray-200', Icon: PauseCircleIcon },
  ended: { className: 'bg-gray-100 text-gray-600 border-gray-200', Icon: XCircleIcon },
}

export const StatusBadge: React.FC<{ subscription: Subscription }> = ({ subscription }) => {
  const badge = statusBadge(subscription)
  const { className, Icon } = BADGES[badge.kind]
  const label = {
    active: 'Active',
    trial: 'Trial',
    cancels: `Cancels on ${badge.endsAt ? format(new Date(badge.endsAt), 'MMM d') : ''}`,
    past_due: 'Past due',
    paused: 'Paused',
    ended: 'Ended',
  }[badge.kind]
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${className}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </span>
  )
}

const button =
  'inline-flex items-center justify-center rounded-lg px-3.5 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent'

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  onCancel,
  onReactivate,
  onManage,
  onUpdateCard,
}) => {
  const { creator, tier } = subscription
  const price = priceCents(subscription)
  const badge = statusBadge(subscription)
  const pastDue = badge.kind === 'past_due'
  const cancelling = badge.kind === 'cancels'
  const renewing = badge.kind === 'active' || badge.kind === 'trial'

  return (
    <article
      className={`group flex flex-col rounded-xl border bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
        pastDue ? 'border-red-200' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start gap-3">
        <CreatorAvatar user={creator} />
        <div className="min-w-0 flex-1">
          <Link
            to={`/creator/${subscription.creator_id}`}
            className="block truncate font-semibold text-gray-900 hover:text-accent"
          >
            {creator ? displayName(creator) : 'Creator'}
          </Link>
          {creator && <p className="truncate text-sm text-gray-500">@{creator.username}</p>}
          {/* Phones: under the name, so a long "Cancels on" badge doesn't squeeze it. */}
          <div className="mt-1.5 sm:hidden">
            <StatusBadge subscription={subscription} />
          </div>
        </div>
        <div className="hidden sm:block">
          <StatusBadge subscription={subscription} />
        </div>
      </div>

      <div className="mt-4 flex items-end justify-between gap-3 rounded-lg bg-gray-50 px-4 py-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Plan</p>
          <p className="truncate font-medium text-gray-900">{tier?.name || 'Subscription'}</p>
        </div>
        {price != null && (
          <p className="whitespace-nowrap text-right">
            <span className="text-xl font-bold text-gray-900">{formatCents(price)}</span>
            <span className="text-sm text-gray-500"> / {subscription.is_annual ? 'year' : 'month'}</span>
          </p>
        )}
      </div>

      <p className="mt-3 text-sm text-gray-600">
        {pastDue ? (
          <span className="text-red-600">Your last payment failed. Update your card to keep access.</span>
        ) : cancelling ? (
          <>
            Access until <span className="font-medium text-gray-900">{day(subscription.current_period_end)}</span>
            . You won&rsquo;t be charged again.
          </>
        ) : renewing ? (
          <>
            {badge.kind === 'trial' ? 'Trial ends, first charge' : 'Renews'}{' '}
            <span className="font-medium text-gray-900">{day(subscription.current_period_end)}</span>
          </>
        ) : (
          <>Ended {day(subscription.current_period_end)}</>
        )}
      </p>

      <div className="mt-auto flex flex-wrap gap-2 pt-4">
        {pastDue && onUpdateCard && (
          <button
            type="button"
            onClick={() => onUpdateCard(subscription)}
            className={`${button} bg-accent text-terminal-bg hover:bg-accent-hover`}
          >
            Update card
          </button>
        )}
        {cancelling && onReactivate && (
          <button
            type="button"
            onClick={() => onReactivate(subscription)}
            className={`${button} bg-accent text-terminal-bg hover:bg-accent-hover`}
          >
            Resume
          </button>
        )}
        {onManage && (
          <button
            type="button"
            onClick={() => onManage(subscription)}
            className={`${button} border border-gray-300 text-gray-700 hover:bg-gray-50`}
          >
            Manage
          </button>
        )}
        {(renewing || pastDue) && onCancel && (
          <button
            type="button"
            onClick={() => onCancel(subscription)}
            className={`${button} text-red-600 hover:bg-red-50`}
          >
            Cancel
          </button>
        )}
      </div>
    </article>
  )
}

export default SubscriptionCard
